import React, { useState, useEffect, useCallback, useRef } from "react";
import { Row, Col, Container, Form, FormGroup } from "reactstrap";
import { Button, Label, Input, ButtonGroup } from "reactstrap";
import DatePicker from "react-date-picker";
import { useDropzone } from "react-dropzone";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import ConfirmationModal from "./modals/ConfirmationModal";
import ProgressModal from "./modals/ProgressModal";
import ErrorModal from "./modals/ErrorModal";
import Jumbotron from "helpers/Jumbotron";
import CustomSelect from "helpers/CustomSelect";
import CustomFeedBack from "helpers/CustomFeedBack";
import * as Constants from "helpers/Constants";
import * as Functions from "helpers/Functions";
import PreviewImage from "helpers/PreviewImage";
import Moment from "react-moment";
import ReCAPTCHA from "react-google-recaptcha";
import NumberFormat from "react-number-format";
import _ from "lodash";
import SVG_mobilite from "./images/mobilite.svg";
import SVG_auditif from "./images/auditif.svg";
import SVG_mental from "./images/mental.svg";
import SVG_signes from "./images/signes.svg";
import SVG_visuel from "./images/visuel.svg";

const accessibilityEntities = [
  { svg: SVG_mobilite, number: 1, name: "Mobilité réduite" },
  { svg: SVG_auditif, number: 2, name: "Déficiences auditives" },
  { svg: SVG_visuel, number: 3, name: "Déficiences visuelles" },
  { svg: SVG_mental, number: 4, name: "Déficiences mentales" },
  { svg: SVG_signes, number: 5, name: "Langue des signes" },
];

const emptyAsso = {
  nom: "",
  description: "",
  adresse: "",
  url: "",
  telephone: "",
  email: "",
  illustration: null,
  key: null,
  activites: [],
};

const emptyActivite = {
  nom: "",
  age_min: "",
  age_max: "",
  type: 0,
  lieu: "",
  date_deb: "",
  date_fin: "",
  jours: "00000000",
  horaires: "",
  accessibility1: -1,
  accessibility2: -1,
  accessibility3: -1,
  accessibility4: -1,
  accessibility5: -1,
};

// const emptyAsso = {
//   nom: "dedede",
//   description: "dedede",
//   adresse: "dededef",
//   url: "https://fr.fr",
//   telephone: "",
//   email: "jh@vanilladome.net",
//   illustration: null,
//   key: null,
//   activites: [],
// };

// const emptyActivite = {
//   nom: "dede",
//   age_min: "2",
//   age_max: "3",
//   type: 3,
//   lieu: "ccdcd",
//   date_deb: "2022-08-22T22:00:00.000Z",
//   date_fin: "2022-09-22T22:00:00.000Z",
//   jours: "10000000",
//   horaires: "dede",
// };

const requiredError = "Ce champs est requis";
const titreMaxLength = 50;
const descriptionMaxLength = 1500;
const invalidURL = "Ceci ne semble pas être une URL valide";
const invalidEmail = "Ceci ne semble pas être un email valide";
const typeError = "Veuillez choisir un type";
const invalidDate = "Date invalide";
const ageMustBeNumber = "L'âge doit être un chiffre";
const inconsistentAges = "Âges incohérents";
const inconsistentDates = "Dates incohérentes";
const fileIntSize = 5 * 1024 * 1024;
const fileSize = `Le fichier ne doit pas depasser ${Functions.formatBytes(fileIntSize)}`;
const imageMinWidth = 400;
const imageMinHeight = 400;
const imageSize = `l'image doit faire au minimum ${imageMinWidth}x${imageMinHeight}`;
const dayError = "Veuillez choisir au moins un jour";
const atLeastOneActivite = "Renseignez au moins une activité";
const maxLength = (limit) => [limit, `Maximum ${limit} caractères`];

export const AssociationsForm = (props) => {
  const [types, setTypes] = useState(null);
  const confirmationModal = useRef(null);
  const progressModal = useRef(null);
  const errorModal = useRef(null);
  const recaptchaRef = useRef(null);

  const updateTypes = useCallback(() => {
    axios
      .get(Constants.TYPES_API)
      .then((res) => {
        if (res.data.status !== "success") {
          alert("erreur de communication avec le serveur");
        } else {
          res.data.data.unshift({ id: 0, intitule: "Aucun" });
          setTypes(res.data.data);
        }
      })
      .catch((error) => alert("erreur de communication avec le serveur"));
  }, []);

  const emptyState = () => {
    recaptchaRef.current.reset();
    reset();
  };

  const validationSchema = yup.object().shape({
    nom: yup
      .string()
      .required(requiredError)
      .max(...maxLength(titreMaxLength)),
    description: yup
      .string()
      .required(requiredError)
      .max(...maxLength(descriptionMaxLength)),
    adresse: yup.string(),
    url: yup.string().url(invalidURL),
    email: yup.string().email(invalidEmail).required(requiredError),
    illustration: yup
      .mixed()
      .test("fileSize", fileSize, (file) => (file ? file.size <= fileIntSize : true))
      .test("imageSize", imageSize, (file) => (file ? validateImageSize(file, imageMinWidth, imageMinHeight) : true)),
    //key: yup.string().nullable().required(requiredError),
    activites: yup
      .array()
      .of(
        yup.object().shape({
          nom: yup.string().required(requiredError),
          age_min: yup.number().typeError(ageMustBeNumber).max(yup.ref("age_max"), inconsistentAges),
          age_max: yup.number().typeError(ageMustBeNumber).min(yup.ref("age_min"), inconsistentAges),
          type: yup.number().notOneOf([0], typeError),
          lieu: yup.string().required(requiredError),
          jours: yup.string().notOneOf(["00000000"], dayError),
          horaires: yup.string().required(requiredError),
          date_deb: yup.date().typeError(invalidDate).required(requiredError),
          date_fin: yup
            .date()
            .typeError(invalidDate)
            .required(requiredError)
            .when(["date_deb"], (date_deb, schema) =>
              date_deb instanceof Date && !isNaN(date_deb) ? schema.min(date_deb, inconsistentDates) : schema
            ),
          accessibility1: yup.number().oneOf([1, 2, 3], requiredError),
          accessibility2: yup.number().oneOf([1, 2, 3], requiredError),
          accessibility3: yup.number().oneOf([1, 2, 3], requiredError),
          accessibility4: yup.number().oneOf([1, 2, 3], requiredError),
          accessibility5: yup.number().oneOf([1, 2, 3], requiredError),
        })
      )
      .min(1, atLeastOneActivite),
  });

  const sendForm = (values) => {
    let associationFields = ["nom", "description", "adresse", "url", "telephone", "email"];
    let fieldNames = ["Nom", "Description", "Adresse", "Url", "Téléphone", "Email"];
    let usedData = _.clone(values);
    usedData.illustration = values.illustration;
    let content = (
      <div>
        {associationFields.map((field, i) => (
          <Row>
            <Col xs={12} md={2}>
              {fieldNames[i]} :
            </Col>
            <Col xs={12} md={10}>
              {usedData[field]}
            </Col>
          </Row>
        ))}
        {usedData.illustration !== null && (
          <>
            <span>Illustration : </span>
            <PreviewImage src={usedData.illustration} />
          </>
        )}
        <br />
        <br />
        {usedData.activites.map((activite) => {
          let shown = {};
          shown.nom = activite.nom;
          for (let index = 0; index < 8; index++) {
            activite["j" + index] = parseInt(activite.jours.charAt(index), 10);
          }

          if (activite.age_min !== -1 && activite.age_max === 199) {
            shown.ages = "À partir de " + activite.age_min;
          } else if (activite.age_min === -1 && activite.age_max !== 199) {
            shown.ages = "Jusque " + activite.age_max + " ans";
          } else if (activite.age_min !== -1 && activite.age_max !== 199) {
            shown.ages = "De " + activite.age_min + " ans à " + activite.age_max + " ans";
          } else if (activite.age_min === -1 && activite.age_max === 199) {
            shown.ages = "Tout âge";
          }

          shown.type = getType(activite.type)[0].intitule;
          let { j0, j1, j2, j3, j4, j5, j6, j7 } = activite;

          if (j7 === 1) {
            shown.jour = "Jour variable";
          } else {
            let nbjours = j0 + j1 + j2 + j3 + j4 + j5 + j6;
            let tmp = [];
            let liste = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];

            [j0, j1, j2, j3, j4, j5, j6].forEach((j, i) => {
              if (j === 1) {
                tmp.push(liste[i]);
              }
            });
            if (nbjours === 1) {
              shown.jour = "Le " + tmp[0];
            } else {
              tmp = tmp.map((j) => (j += "s"));
              shown.jour = "Les " + tmp.join(", ");
              let n = shown.jour.lastIndexOf(", ");
              let part1 = shown.jour.slice(0, n);
              let part2 = shown.jour.slice(n + 2, shown.jour.length);
              shown.jour = part1 + " et " + part2;
            }
          }

          shown.lieu = activite.lieu;
          shown.horaires = activite.horaires;

          return (
            <Row key={"" + activite.id + shown.nom}>
              <Col md={1}></Col>
              <Col md={11}>
                <h5 style={{ textDecoration: "underline" }}>
                  Activité {activite.id} / {shown.nom} - {shown.ages}
                </h5>
                Période : du <Moment format="DD/MM/YYYY">{activite.date_deb}</Moment> au{" "}
                <Moment format="DD/MM/YYYY">{activite.date_fin}</Moment>
                <h6>{shown.type}</h6>
                {shown.jour} {shown.horaires.toLowerCase()}
                <br />
                {shown.lieu}
              </Col>
            </Row>
          );
        })}
        <br />
        <br />
        <strong>
          En validant, j'accepte que la mairie de Metz utilise les données transmises dans le cadre de ce traitement
        </strong>
      </div>
    );

    confirmationModal.current.changeContent("Récapitulatif", content, "primary", "Valider", (response) => {
      if (response) {
        // Envoyer sur les internet
        usedData.activites.forEach((activite) => {
          if (activite.age_min === "") {
            activite.age_min = 0;
          }
          if (activite.age_max === "") {
            activite.age_max = 99;
          }
          activite.date_deb = activite.date_deb.toISOString().slice(0, 19).replace("T", " ");
          activite.date_fin = activite.date_fin.toISOString().slice(0, 19).replace("T", " ");

          delete activite.jours;
          delete activite.id;
          delete activite.editing;
          delete activite.minute_deb;
          delete activite.minute_fin;
        });

        var formData = new FormData();
        var config = {};
        // var fileInputElement = document.getElementById("illustration");

        config.headers = { "content-type": "multipart/form-data" };

        if (usedData.illustration !== null) {
          formData.append("file", usedData.illustration);
        }
        delete usedData.illustration;
        formData.append("form", JSON.stringify(usedData));

        progressModal.current.show({
          onClose: () => {
            // document.getElementById("illustration").value = "";
            emptyState();
          },
        });

        config.onUploadProgress = (progressEvent) => {
          var percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          progressModal.current.setProgress(percentage);
        };
        // console.log(usedData);
        axios
          .post(Constants.ASSOCIATIONS_API, formData, config)
          .then((res) => {
            // if (res.data.status !== "success") {
            //   alert("erreur de communication avec le serveur");
            // } else {
            //   emptyState();
            // }
          })
          .catch((error) => console.log(error));
      }
    });
  };
  // confirmationModal.current.changeContent(
  //   "Erreurs",
  //   "Veuillez corriger les champs en rouge",
  //   "danger",
  //   "Annuler",
  //   (response) => {}
  // );

  const formOptions = {
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: emptyAsso,
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    trigger,
    reset,
  } = useForm(formOptions);

  const { fields, append, remove } = useFieldArray({ name: "activites", control });

  const illustration = watch("illustration");
  const activites = watch("activites");
  // const values = watch();

  const getType = (id_type) => types.filter((l) => l.id === id_type);
  const isActive = (jours, n) => (jours.charAt(n) === "1" ? true : false);
  const invertActive = (jours, n) =>
    jours.substring(0, n) + (jours.charAt(n) === "1" ? "0" : "1") + jours.substring(n + 1);
  const invertActiveForId = (value, index, jour) => {
    let newJours = invertActive(value, jour);

    if (jour === 7) {
      newJours = "0000000" + newJours.charAt(7);
    } else {
      newJours = newJours.substring(0, 7) + "0";
    }
    setValue(`activites.${index}.jours`, newJours);
    trigger(`activites.${index}.jours`);
  };

  const validateImageSize = (file, minWidth, minHeight) => {
    var img = new Image();

    img.src = window.URL.createObjectURL(file);

    return new Promise((resolve) => {
      img.onerror = (e) => {
        resolve(false);
      };

      img.onload = (e) => {
        let width = img.naturalWidth;
        let height = img.naturalHeight;

        window.URL.revokeObjectURL(img.src);
        if (width < minWidth || height < minHeight) resolve(false);
        resolve(true);
      };
    });
  };

  const showError = () => {
    errorModal.current.show();
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles) => {
      setValue("illustration", acceptedFiles[0]);
      trigger("illustration");
    },
    multiple: false,
  });

  useEffect(() => {
    // updateLieux();
    // captchaInstance.reset();
    updateTypes();
    return () => {};
  }, [updateTypes]);

  return (
    <Container>
      <h2>Votre association :</h2>

      <Form id="eventForm" onSubmit={handleSubmit(sendForm, showError)}>
        <FormGroup row>
          <Label for="nom" sm={2}>
            <strong>Nom et acronyme</strong>
          </Label>
          <Col sm={10}>
            <Controller
              name="nom"
              control={control}
              render={({ field }) => <Input {...field} invalid={errors.hasOwnProperty("nom")} />}
            />
            <CustomFeedBack>{errors.nom?.message}</CustomFeedBack>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="description" sm={2}>
            <strong>Décrivez votre association, vos actions.</strong>
          </Label>
          <Col sm={10}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Input
                  type="textarea"
                  style={{ height: "200px" }}
                  {...field}
                  invalid={errors.hasOwnProperty("description")}
                />
              )}
            />
            <CustomFeedBack>{errors.description?.message}</CustomFeedBack>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="adresse" sm={2}>
            Adresse
          </Label>
          <Col sm={10}>
            <Controller
              name="adresse"
              control={control}
              render={({ field }) => <Input {...field} invalid={errors.hasOwnProperty("adresse")} />}
            />
            <CustomFeedBack>{errors.adresse?.message}</CustomFeedBack>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="url" sm={2}>
            Site, Facebook, ...
          </Label>
          <Col sm={10}>
            <Controller
              name="url"
              control={control}
              render={({ field }) => <Input {...field} invalid={errors.hasOwnProperty("url")} />}
            />
            <CustomFeedBack>{errors.url?.message}</CustomFeedBack>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="telephone" sm={2}>
            Téléphone
          </Label>
          <Col sm={10}>
            <Controller
              name="telephone"
              control={control}
              render={({ field }) => (
                <NumberFormat
                  {...field}
                  invalid={errors.hasOwnProperty("telephone")}
                  customInput={Input}
                  format="## ## ## ## ##"
                />
              )}
            />
            <CustomFeedBack>{errors.telephone?.message}</CustomFeedBack>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="email" sm={2}>
            <strong>Email</strong>
          </Label>
          <Col sm={10}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => <Input {...field} invalid={errors.hasOwnProperty("email")} />}
            />
            <CustomFeedBack>{errors.email?.message}</CustomFeedBack>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="illustration" sm={2}>
            Illustration
          </Label>
          <Col md={illustration !== null ? 6 : 10}>
            <div {...getRootProps()} className={isDragActive ? "drop-over" : "drop"}>
              <input {...getInputProps()} />
              <span>Cliquer ou glisser un fichier ici</span>
            </div>
            <CustomFeedBack>{errors.illustration?.message}</CustomFeedBack>
          </Col>
          {illustration !== null && (
            <>
              <Col md={2}>
                <Button
                  onClick={() => {
                    setValue("illustration", null);
                  }}>
                  retirer l'image
                </Button>
              </Col>
              <Col md={2}>
                <PreviewImage src={illustration} />
              </Col>
            </>
          )}
        </FormGroup>
        <h2>
          Vos activités :{" "}
          <Button
            color="secondary"
            size="sm"
            onClick={() => {
              append(emptyActivite);
              trigger("activites");
            }}>
            <span>+</span>
          </Button>
        </h2>
        <FormGroup row>
          {fields.map((activite, i) => {
            return (
              <>
                <Jumbotron
                  key={i}
                  style={{
                    backgroundColor: "white",
                    padding: "1em",
                    border: "4px solid grey",
                    marginBottom: "0.5em",
                  }}>
                  <h4 style={{ textDecoration: "underline" }}>
                    Activité {i + 1} / {activites[i].nom}
                  </h4>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for={"nom" + activite.id}>
                          <strong>Nom de l'activité</strong>
                        </Label>
                        <Controller
                          name={`activites.${i}.nom`}
                          control={control}
                          render={({ field }) => (
                            <Input {...field} invalid={errors.activites?.[i]?.hasOwnProperty("nom")} />
                          )}
                        />
                        <CustomFeedBack>{errors.activites?.[i]?.nom?.message}</CustomFeedBack>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <Label for={"age_min" + activite.id}>
                        <strong>Age minimum</strong> (sans{" "}
                        <Input
                          type="checkbox"
                          // checked={activites[i].age_min === "-1"}
                          onClick={() => {
                            setValue(`activites.${i}.age_min`, activites[i].age_min === "-1" ? "" : "-1");
                            trigger(`activites.${i}.age_min`);
                            trigger(`activites.${i}.age_max`);
                          }}
                        />
                        )
                      </Label>
                      <Controller
                        name={`activites.${i}.age_min`}
                        control={control}
                        render={({ field }) => (
                          <>
                            <Input
                              type={activites[i].age_min === "-1" ? "hidden" : "text"}
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                trigger(`activites.${i}.age_max`);
                              }}
                              invalid={errors.activites?.[i]?.hasOwnProperty("age_min")}
                            />
                            {activites[i].age_min === "-1" && (
                              <div style={{ fontWeight: "bold" }}>Pas d'âge minimum</div>
                            )}
                          </>
                        )}
                      />
                      <CustomFeedBack>{errors.activites?.[i]?.age_min?.message}</CustomFeedBack>
                    </Col>
                    <Col md={3}>
                      <Label for={"age_max" + activite.id}>
                        <strong>Age maximum</strong> (sans{" "}
                        <Input
                          type="checkbox"
                          onClick={() => {
                            setValue(`activites.${i}.age_max`, activites[i].age_max === "199" ? "" : "199");
                            trigger(`activites.${i}.age_max`);
                            trigger(`activites.${i}.age_min`);
                          }}
                        />
                        )
                      </Label>
                      <Controller
                        name={`activites.${i}.age_max`}
                        control={control}
                        render={({ field }) => (
                          <>
                            <Input
                              type={activites[i].age_max === "199" ? "hidden" : "text"}
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                trigger(`activites.${i}.age_min`);
                              }}
                              invalid={errors.activites?.[i]?.hasOwnProperty("age_max")}
                            />
                            {activites[i].age_max === "199" && (
                              <div style={{ fontWeight: "bold" }}>Pas d'âge maximum</div>
                            )}
                          </>
                        )}
                      />
                      <CustomFeedBack>{errors.activites?.[i]?.age_max?.message}</CustomFeedBack>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Label for={"type" + activite.id}>
                        <strong>Type</strong>
                      </Label>
                      <Controller
                        name={`activites.${i}.type`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <CustomSelect
                            options={types}
                            defaultValue={getType(value)}
                            getOptionLabel={(o) => o.intitule}
                            getOptionValue={(o) => o.id}
                            invalid={errors.activites?.[i]?.hasOwnProperty("type")}
                            onChange={(e) => onChange(e.id)}></CustomSelect>
                        )}
                      />
                      <CustomFeedBack>{errors.activites?.[i]?.type?.message}</CustomFeedBack>
                    </Col>
                    <Col md={4}>
                      <Label for={"lieu" + activite.id}>
                        <strong>Lieu</strong> (un seul)
                      </Label>
                      <Controller
                        name={`activites.${i}.lieu`}
                        control={control}
                        render={({ field }) => (
                          <Input {...field} invalid={errors.activites?.[i]?.hasOwnProperty("lieu")} />
                        )}
                      />
                      <CustomFeedBack>{errors.activites?.[i]?.lieu?.message}</CustomFeedBack>
                    </Col>
                    <Col md={2}>
                      <Label for={"date_deb" + activite.id}>
                        <strong>Début période</strong>
                      </Label>
                      <Controller
                        name={`activites.${i}.date_deb`}
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            {...field}
                            className={`form-control ${
                              errors.activites?.[i]?.hasOwnProperty("date_deb") ? "is-invalid-custom" : ""
                            }`}
                          />
                        )}
                      />
                      <CustomFeedBack>{errors.activites?.[i]?.date_deb?.message}</CustomFeedBack>
                    </Col>
                    <Col md={2}>
                      <Label for={"date_fin" + activite.id}>
                        <strong>Fin période</strong>
                      </Label>
                      <Controller
                        name={`activites.${i}.date_fin`}
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            {...field}
                            className={`form-control ${
                              errors.activites?.[i]?.hasOwnProperty("date_fin") ? "is-invalid-custom" : ""
                            }`}
                          />
                        )}
                      />
                      <CustomFeedBack>{errors.activites?.[i]?.date_fin?.message}</CustomFeedBack>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Label for={"jour" + activite.id}>
                        <strong>Jour</strong>
                      </Label>
                      <br />
                      <Controller
                        name={`activites.${i}.jours`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <>
                            <Button
                              color={errors.activites?.[i]?.hasOwnProperty("jours") ? "danger" : "primary"}
                              size="sm"
                              type="button"
                              outline
                              style={{ paddingBottom: "4px", marginRight: "4px" }}
                              active={isActive(value, 7)}
                              onClick={() => invertActiveForId(value, i, 7)}>
                              Variable
                            </Button>
                            <ButtonGroup size="sm">
                              {["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"].map((jour, j) => {
                                return (
                                  <Button
                                    key={i + "" + jour + j}
                                    color={errors.activites?.[i]?.hasOwnProperty("jours") ? "danger" : "primary"}
                                    outline
                                    active={isActive(value, j)}
                                    onClick={() => invertActiveForId(value, i, j)}>
                                    {jour}
                                  </Button>
                                );
                              })}
                            </ButtonGroup>
                          </>
                        )}
                      />
                      <br />
                      <CustomFeedBack>{errors.activites?.[i]?.jours?.message}</CustomFeedBack>
                    </Col>
                    <Col md={8}>
                      <FormGroup>
                        <Label for={"horaires" + activite.id}>
                          <strong>Description des horaires et des activités</strong>
                        </Label>
                        <Controller
                          name={`activites.${i}.horaires`}
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="textarea"
                              rows="4"
                              invalid={errors.activites?.[i]?.hasOwnProperty("horaires")}
                            />
                          )}
                        />
                        <CustomFeedBack>{errors.activites?.[i]?.horaires?.message}</CustomFeedBack>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} sm={12}>
                      <h5>Accessibilité :</h5>
                    </Col>
                    {accessibilityEntities.map((e) => (
                      <Col xs={6} sm={4} key={e.number + activite.id}>
                        <FormGroup row>
                          <Label for={"accessibility" + e.number + activite.id} sm={7}>
                            <img src={e.svg} alt="" style={{ width: "1em" }} /> <strong>{e.name}</strong>
                          </Label>
                          <Col sm={5}>
                            <Controller
                              name={`activites.${i}.accessibility${e.number}`}
                              control={control}
                              render={({ field: { value, onChange } }) => (
                                <ButtonGroup size="sm">
                                  <Button
                                    color={
                                      errors.activites?.[i]?.hasOwnProperty("accessibility" + e.number)
                                        ? "danger"
                                        : "primary"
                                    }
                                    outline
                                    onClick={() => onChange(1)}
                                    active={value === 1}>
                                    Oui
                                  </Button>
                                  <Button
                                    color={
                                      errors.activites?.[i]?.hasOwnProperty("accessibility" + e.number)
                                        ? "danger"
                                        : "primary"
                                    }
                                    outline
                                    onClick={() => onChange(2)}
                                    active={value === 2}>
                                    Non
                                  </Button>
                                  <Button
                                    color={
                                      errors.activites?.[i]?.hasOwnProperty("accessibility" + e.number)
                                        ? "danger"
                                        : "primary"
                                    }
                                    outline
                                    onClick={() => onChange(3)}
                                    active={value === 3}>
                                    N.C.
                                  </Button>
                                </ButtonGroup>
                              )}
                            />
                            <CustomFeedBack>
                              {errors.activites?.[i]?.["accessibility" + e.number]?.message}
                            </CustomFeedBack>
                          </Col>
                        </FormGroup>
                      </Col>
                    ))}
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <Button
                        color="danger"
                        onClick={() =>
                          window.confirm("Êtes vous sûr de vouloir supprimer cette activité ?") ? remove(i) : null
                        }>
                        Supprimer cette activité
                      </Button>
                    </Col>
                  </Row>
                </Jumbotron>
              </>
            );
          })}
          <Col sm={10}></Col>
        </FormGroup>
        <CustomFeedBack>{errors.activites?.message}</CustomFeedBack>
        <FormGroup row>
          <Label for="infos" sm={2}>
            Vérification<sup>*</sup>
          </Label>
          <Col sm={10}>
            <ReCAPTCHA
              ref={recaptchaRef}
              onChange={(key) => {
                setValue("key", key);
                trigger("key");
              }}
              sitekey="6LdPjAwTAAAAAKJlyaceYboOlDfebAH9t1FFz2sn"
            />
            <CustomFeedBack>{errors.key?.message}</CustomFeedBack>{" "}
          </Col>
        </FormGroup>
        <Row>
          <Col sm="12" md={{ size: 6, offset: 3 }} style={{ paddingTop: "1em" }}>
            <Button color="primary" className="btn-block" type="submit">
              Valider votre inscription
            </Button>
          </Col>
        </Row>
      </Form>
      {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}
      <ConfirmationModal ref={confirmationModal} />
      <ProgressModal ref={progressModal} />
      <ErrorModal ref={errorModal} />
    </Container>
  );
};

export default AssociationsForm;
